import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PrivacyPolicyModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  const handleOverlayClick = (e) => {
    if (e?.target === e?.currentTarget) {
      onClose();
    }
  };

  const handleKeyDown = (e) => {
    if (e?.key === 'Escape') {
      onClose();
    }
  };

  return (
    <div
      className="fixed inset-0 z-3000 flex items-center justify-center bg-background/80 backdrop-blur-sm p-4 animate-fade-in"
      onClick={handleOverlayClick}
      onKeyDown={handleKeyDown}
      role="dialog"
      aria-modal="true"
      aria-labelledby="privacy-policy-title"
    >
      <div className="w-full max-w-2xl max-h-[90vh] bg-card rounded-xl shadow-2xl border border-border overflow-hidden animate-scale-in">
        <div className="sticky top-0 bg-card border-b border-border p-4 sm:p-6 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 sm:w-12 sm:h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                <Icon name="Shield" size={20} color="var(--color-primary)" />
              </div>
              <div>
                <h2 id="privacy-policy-title" className="text-lg sm:text-xl font-semibold text-foreground">
                  Política de Protección de Datos
                </h2>
                <p className="text-xs sm:text-sm text-muted-foreground">
                  Normativa GDPR y LOPD
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-muted transition-smooth focus-ring"
              aria-label="Cerrar política"
            >
              <Icon name="X" size={20} color="var(--color-muted-foreground)" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto max-h-[calc(90vh-180px)] p-4 sm:p-6 space-y-6">
          <section>
            <h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Info" size={18} color="var(--color-primary)" />
              Responsable del Tratamiento
            </h3>
            <div className="bg-muted/30 rounded-lg p-4 space-y-2 text-sm">
              <p className="text-foreground"><strong>Empresa:</strong> XAIKUS Medical Solutions S.L.</p>
              <p className="text-foreground"><strong>CIF:</strong> B-12345678</p>
              <p className="text-foreground"><strong>Dirección:</strong> Calle Médica 123, 28001 Madrid, España</p>
              <p className="text-foreground"><strong>Email:</strong> protecciondatos@xaikus.es</p>
            </div>
          </section>

          <section>
            <h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Database" size={18} color="var(--color-primary)" />
              Finalidad del Tratamiento
            </h3>
            <div className="space-y-3 text-sm text-foreground">
              <p>Los datos personales recogidos serán tratados con las siguientes finalidades:</p>
              <ul className="space-y-2 ml-4">
                <li className="flex items-start gap-2">
                  <Icon name="CheckCircle2" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                  <span>Gestión de participación en eventos y congresos médicos organizados por XAIKUS</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="CheckCircle2" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                  <span>Entrega y seguimiento de premios obtenidos en actividades de gamificación</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="CheckCircle2" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                  <span>Envío de información sobre productos, servicios y eventos médicos (con consentimiento)</span>
                </li>
                <li className="flex items-start gap-2">
                  <Icon name="CheckCircle2" size={16} color="var(--color-success)" className="mt-0.5 flex-shrink-0" />
                  <span>Análisis estadístico de participación y mejora de experiencias en eventos</span>
                </li>
              </ul>
            </div>
          </section>

          <section>
            <h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Lock" size={18} color="var(--color-primary)" />
              Legitimación y Conservación
            </h3>
            <div className="space-y-3 text-sm text-foreground">
              <p><strong>Base legal:</strong> Consentimiento explícito del interesado para el tratamiento de datos personales y envío de comunicaciones comerciales.</p>
              <p><strong>Conservación:</strong> Los datos se conservarán durante el tiempo necesario para cumplir con las finalidades descritas y, posteriormente, durante los plazos legales establecidos por la normativa aplicable.</p>
            </div>
          </section>

          <section>
            <h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Users" size={18} color="var(--color-primary)" />
              Derechos del Usuario
            </h3>
            <div className="space-y-3 text-sm text-foreground">
              <p>Como titular de los datos, tienes derecho a:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg">
                  <Icon name="Eye" size={16} color="var(--color-primary)" />
                  <span>Acceso a tus datos</span>
                </div>
                <div className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg">
                  <Icon name="Edit" size={16} color="var(--color-primary)" />
                  <span>Rectificación</span>
                </div>
                <div className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg">
                  <Icon name="Trash2" size={16} color="var(--color-primary)" />
                  <span>Supresión</span>
                </div>
                <div className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg">
                  <Icon name="Ban" size={16} color="var(--color-primary)" />
                  <span>Oposición</span>
                </div>
                <div className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg">
                  <Icon name="Pause" size={16} color="var(--color-primary)" />
                  <span>Limitación</span>
                </div>
                <div className="flex items-center gap-2 p-2 bg-muted/30 rounded-lg">
                  <Icon name="Download" size={16} color="var(--color-primary)" />
                  <span>Portabilidad</span>
                </div>
              </div>
              <p className="pt-2">Para ejercer estos derechos, contacta con: <strong>protecciondatos@xaikus.es</strong></p>
            </div>
          </section>

          <section>
            <h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="AlertTriangle" size={18} color="var(--color-warning)" />
              Seguridad de los Datos
            </h3>
            <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 text-sm text-foreground">
              <p>XAIKUS implementa medidas técnicas y organizativas apropiadas para garantizar la seguridad de tus datos personales y protegerlos contra acceso no autorizado, pérdida o destrucción accidental.</p>
            </div>
          </section>

          <section>
            <h3 className="text-base sm:text-lg font-semibold text-foreground mb-3 flex items-center gap-2">
              <Icon name="Scale" size={18} color="var(--color-primary)" />
              Autoridad de Control
            </h3>
            <div className="text-sm text-foreground">
              <p>Tienes derecho a presentar una reclamación ante la Agencia Española de Protección de Datos (AEPD) si consideras que el tratamiento de tus datos personales no cumple con la normativa vigente.</p>
              <p className="mt-2"><strong>Web:</strong> www.aepd.es</p>
            </div>
          </section>
        </div>

        <div className="sticky bottom-0 bg-card border-t border-border p-4 sm:p-6">
          <Button
            variant="default"
            fullWidth
            onClick={onClose}
            iconName="CheckCircle2"
            iconPosition="left"
          >
            He leído la política
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicyModal;